import { useTranslation } from "react-i18next";
import { TrendingUp, TrendingDown, Minus, Wallet } from "lucide-react";
import { Card, CardContent } from "@tabipla/ui";
import { useGroupBalance } from "@/application/hooks/useExpenses";

interface GroupBalanceCardProps {
  groupId: string;
  onNavigateToExpenses: () => void;
}

export function GroupBalanceCard({ groupId, onNavigateToExpenses }: GroupBalanceCardProps) {
  const { t } = useTranslation();
  const { data: balance, isLoading } = useGroupBalance(groupId);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-4">
          <div className="h-5 w-24 rounded bg-muted" />
          <div className="mt-2 h-8 w-32 rounded bg-muted" />
        </CardContent>
      </Card>
    );
  }

  // Don't show if no balance data or balance is zero
  if (!balance || balance.balance === 0) {
    return null;
  }

  const isPositive = balance.balance > 0;
  const isNegative = balance.balance < 0;

  return (
    <Card
      className="cursor-pointer transition-all hover:bg-accent hover:shadow-sm active:scale-[0.99]"
      onClick={onNavigateToExpenses}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Wallet className="h-4 w-4" />
          <span>{t("groups.balanceCard.title")}</span>
        </div>

        <div className="mt-2">
          {isPositive ? (
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="text-xl font-bold text-green-600 dark:text-green-400">
                {formatAmount(balance.balance)}
              </span>
            </div>
          ) : isNegative ? (
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-xl font-bold text-red-600 dark:text-red-400">
                {formatAmount(Math.abs(balance.balance))}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Minus className="h-5 w-5 text-muted-foreground" />
              <span className="text-xl font-bold text-muted-foreground">
                {t("groups.balanceCard.even")}
              </span>
            </div>
          )}

          <p className="mt-1 text-sm text-muted-foreground">
            {isPositive && t("groups.balanceCard.receivable")}
            {isNegative && t("groups.balanceCard.payable")}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
